import { useLazyQuery, useQuery } from "@apollo/client/react";
import { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router";

import { PollingIntervalGroups } from "apollo/constants";
import rethrowIfNotAborted from "apollo/rethrowIfNotAborted";
import usePollingInterval from "apollo/usePolledQuery/usePollingInterval";
import { makeRunBackUrl } from "components/Breadcrumbs/helpers";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import NotFoundPage from "components/error/NotFoundPage";
import FlashContext from "components/FlashMessages/FlashContext";
import PageLoading from "components/loading/PageLoading";
import { AnalyticsPageStack } from "hooks/useAnalytics/pages/stack";
import useErrorHandle from "hooks/useErrorHandle";
import usePrevious from "hooks/usePrevious";
import useTypedContext from "hooks/useTypedContext";
import { AnsibleHost, RunType, Stack } from "types/generated";
import {
  shouldContinuePollingNextRun,
  shouldStopPolling,
  shouldStopPollingNextRun,
} from "utils/run";
import { isAnsibleStackVendor } from "utils/stack";
import Run from "components/Run";
import useRunObservabilityEnabled from "shared/Run/useRunObservabilityEnabled";

import { StackContext } from "../Context";
import { getStacksBackUrl } from "../helpers";
import { GET_NEXT_RUN_INFO, GET_RUN } from "./gql";

const StackRun = () => {
  const navigate = useNavigate();
  const { runId } = useParams<{ runId: string }>();

  const { reportSuccess } = useTypedContext(FlashContext);
  const { stack, stackUrl, configurationManagementViewEnabled } = useTypedContext(StackContext);
  const pollIntervalRun = useRef(0);
  pollIntervalRun.current = usePollingInterval(GET_RUN, PollingIntervalGroups.SingleEntities);
  const pollIntervalNextRun = useRef(0);
  pollIntervalNextRun.current = usePollingInterval(
    GET_NEXT_RUN_INFO,
    PollingIntervalGroups.SingleEntities
  );

  const includeAnsibleHosts = !!(isAnsibleStackVendor(stack) && configurationManagementViewEnabled);
  const includeObservabilityStatus = useRunObservabilityEnabled();

  // eslint-disable-next-line spacelift/use-polled-query
  const { data, error, loading, startPolling, stopPolling } = useQuery<{
    stack: Stack;
    ansibleHosts?: AnsibleHost[];
  }>(GET_RUN, {
    // network-only keeps `loading` off the Tasks tab gate below; the repo default
    // (cache-and-network) would make it flicker on every cached revisit.
    fetchPolicy: "network-only",
    variables: {
      stackId: stack.id,
      runId,
      includeAnsibleHosts,
      ansibleHostsInput: { stack: stack.id, run: runId },
      includeObservabilityStatus,
    },
  });

  // eslint-disable-next-line spacelift/use-polled-query
  const [
    nextRunStart,
    {
      data: nextRunData,
      error: nextRunError,
      startPolling: nextRunStartPolling,
      stopPolling: nextRunStopPolling,
      called: nextRunCalled,
    },
  ] = useLazyQuery<{
    stack: Stack;
  }>(GET_NEXT_RUN_INFO, {
    fetchPolicy: "network-only",
  });

  useEffect(
    () => {
      startPolling(pollIntervalRun.current);
      if (nextRunData?.stack?.run?.id === runId) {
        nextRunStopPolling();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [runId]
  );

  const runData = nextRunData?.stack?.run || data?.stack?.run;
  // fixes browser back case
  const previousRunID = usePrevious(runId);
  const previousNextValue = usePrevious<boolean | "loading">(
    runData && runId === previousRunID ? !!runData.next?.id : "loading",
    runId !== previousRunID
  );
  const isTask = data?.stack?.run?.type === RunType.Task;
  const isProposedRun = data?.stack?.run?.type === RunType.Proposed;

  const handleRedirect = (runUrl: string) => {
    navigate(runUrl);
  };

  useEffect(() => {
    if (previousNextValue !== "loading") {
      if (!previousNextValue && !!runData?.next?.id) {
        const redirectUrl = `${stackUrl}/run/${runData.next?.id}`;
        reportSuccess({
          message: "A newer run is now available",
          actionTitle: "Go to run",
          actionCallback: () => handleRedirect(redirectUrl),
          timeout: 30000,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousNextValue, runData]);

  useEffect(() => {
    const run = data?.stack?.run;

    if (runId && run?.id === runId && shouldStopPolling(run)) {
      stopPolling();
      if (shouldContinuePollingNextRun(run)) {
        nextRunStart({ variables: { stackId: stack.id, runId } }).catch(rethrowIfNotAborted);
        nextRunStartPolling(pollIntervalNextRun.current);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.stack?.run]);

  useEffect(() => {
    if (
      runId &&
      nextRunData?.stack?.run?.id === runId &&
      shouldStopPollingNextRun(nextRunData.stack.run)
    ) {
      nextRunStopPolling();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextRunData?.stack?.run]);

  useBreadcrumbs(
    [
      {
        title: "Stacks",
        link: getStacksBackUrl(),
      },
      {
        title: stack.name,
        link: makeRunBackUrl(stackUrl, isTask, isProposedRun, data?.stack?.run?.driftDetection),
      },
      {
        title: (isTask ? data?.stack?.run?.command : data?.stack?.run?.title) || "",
      },
    ],
    [data?.stack?.run?.title, data?.stack?.run?.command, data?.stack?.run?.driftDetection]
  );

  const ErrorContent = useErrorHandle(error || nextRunError);

  if (ErrorContent) {
    stopPolling();
    if (nextRunCalled) {
      nextRunStopPolling();
    }
    return ErrorContent;
  }

  if (loading && !data?.stack?.run) {
    return <PageLoading />;
  }

  if (!data?.stack?.run) {
    return <NotFoundPage />;
  }

  return (
    <Run
      run={data?.stack?.run}
      stack={stack}
      analyticsPage={AnalyticsPageStack.StacksRunView}
      configurationManagementViewEnabled={configurationManagementViewEnabled}
      hasAnsibleDetails={includeAnsibleHosts ? !!data?.ansibleHosts?.length : undefined}
    />
  );
};

export default StackRun;
